const SELECTOR = '[data-commerce-rules]';
const cartUrl = () => window.Theme?.routes?.cart_url ? `${window.Theme.routes.cart_url}.js` : `${window.Shopify?.routes?.root || '/'}cart.js`;

let cartPromise = null;
function getCart() {
  if (cartPromise) return cartPromise;
  cartPromise = fetch(cartUrl(), {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  })
    .then(async (response) => {
      const cart = await response.json();
      if (!response.ok || cart?.status) throw new Error(cart?.description || cart?.message || 'Unable to load cart');
      return cart;
    })
    .finally(() => {
      cartPromise = null;
    });
  return cartPromise;
}

const splitList = (value) => String(value || '')
  .split(/[\n,|]+/)
  .map((item) => item.trim().toLowerCase())
  .filter(Boolean);

const includesAny = (haystack, needles) => !needles.length || needles.some((needle) => haystack.includes(needle));
const includesAll = (haystack, needles) => !needles.length || needles.every((needle) => haystack.includes(needle));

function deviceMatches(rule) {
  if (!rule || rule === 'all') return true;
  const width = window.innerWidth;
  if (rule === 'mobile') return width < 750;
  if (rule === 'tablet') return width >= 750 && width < 990;
  if (rule === 'desktop') return width >= 990;
  if (rule === 'mobile_tablet') return width < 990;
  if (rule === 'tablet_desktop') return width >= 750;
  return true;
}

function dateMatches(startValue, endValue) {
  const now = Date.now();
  const start = startValue ? Date.parse(startValue) : NaN;
  const end = endValue ? Date.parse(endValue) : NaN;
  if (Number.isFinite(start) && now < start) return false;
  if (Number.isFinite(end) && now > end) return false;
  return true;
}

function baseRulesMatch(element) {
  const data = element.dataset;
  if (data.ruleEnabled === 'false') return false;

  if (data.ruleCustomer && data.ruleCustomer !== 'all' && data.ruleCustomer !== data.customerState) return false;
  if (!deviceMatches(data.ruleDevice)) return false;

  const url = window.location.href.toLowerCase();
  const includeUrls = splitList(data.ruleUrlInclude);
  const excludeUrls = splitList(data.ruleUrlExclude);
  if (includeUrls.length && !includeUrls.some((part) => url.includes(part))) return false;
  if (excludeUrls.some((part) => url.includes(part))) return false;

  const template = String(document.querySelector('main[data-template]')?.dataset.template || '').toLowerCase();
  const templates = splitList(data.ruleTemplates);
  if (templates.length && !templates.some((value) => template === value || template.startsWith(`${value}.`) || template.includes(value))) return false;

  const locales = splitList(data.ruleLocales);
  if (locales.length && !locales.includes(String(data.locale || '').toLowerCase())) return false;
  const countries = splitList(data.ruleCountries);
  if (countries.length && !countries.includes(String(data.country || '').toLowerCase())) return false;

  const tags = splitList(data.productTags);
  if (!includesAny(tags, splitList(data.ruleProductTagsAny))) return false;
  if (!includesAll(tags, splitList(data.ruleProductTagsAll))) return false;
  if (splitList(data.ruleProductTagsNone).some((tag) => tags.includes(tag))) return false;

  const vendorRule = String(data.ruleProductVendor || '').trim().toLowerCase();
  if (vendorRule && !String(data.productVendor || '').toLowerCase().includes(vendorRule)) return false;
  const typeRule = String(data.ruleProductType || '').trim().toLowerCase();
  if (typeRule && !String(data.productType || '').toLowerCase().includes(typeRule)) return false;
  if (data.ruleRequireAvailable === 'true' && data.productAvailable !== 'true') return false;

  if (!dateMatches(data.ruleStart, data.ruleEnd)) return false;
  return true;
}

async function cartRulesMatch(element) {
  const data = element.dataset;
  const minQuantity = Number(data.ruleCartMinQuantity || 0);
  const minTotal = Number(data.ruleCartMinTotal || 0);
  const state = data.ruleCartState || 'any';
  if (minQuantity <= 0 && minTotal <= 0 && state === 'any') return true;

  const cart = await getCart();
  if (minQuantity > 0 && Number(cart.item_count || 0) < minQuantity) return false;
  if (minTotal > 0 && Number(cart.total_price || 0) < minTotal) return false;
  if (state === 'empty' && Number(cart.item_count || 0) !== 0) return false;
  if (state === 'not_empty' && Number(cart.item_count || 0) === 0) return false;
  return true;
}

async function evaluate(element) {
  if (!(element instanceof HTMLElement)) return;
  const showInEditor = element.dataset.ruleShowInEditor !== 'false';
  if (window.Shopify?.designMode && showInEditor) {
    element.hidden = false;
    element.classList.remove('commerce-rules-hidden');
    return;
  }

  let visible = baseRulesMatch(element);
  if (visible) {
    try {
      visible = await cartRulesMatch(element);
    } catch (error) {
      console.warn('[commerce-section-rules] Cart condition check failed:', error);
      visible = false;
    }
  }

  element.hidden = !visible;
  element.classList.toggle('commerce-rules-hidden', !visible);
  element.dispatchEvent(new CustomEvent('commerce:visibility-change', { detail: { visible } }));
}

function evaluateAll(root = document) {
  root.querySelectorAll(SELECTOR).forEach((element) => evaluate(element));
}

let resizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => evaluateAll(), 120);
});
document.addEventListener('theme:cart-synced', () => evaluateAll());
document.addEventListener('shopify:section:load', (event) => evaluateAll(event.target instanceof Element ? event.target : document));
document.addEventListener('DOMContentLoaded', () => evaluateAll());
if (document.readyState !== 'loading') evaluateAll();

const BLOCK_SELECTOR = '[data-commerce-block-rules]';

function blockBaseRulesMatch(element) {
  const data = element.dataset;
  if (data.blockRuleEnabled === 'false') return false;
  if (data.blockRuleCustomer && data.blockRuleCustomer !== 'all' && data.blockRuleCustomer !== data.blockCustomerState) return false;
  const url = window.location.href.toLowerCase();
  const includeUrls = splitList(data.blockRuleUrlInclude);
  const excludeUrls = splitList(data.blockRuleUrlExclude);
  if (includeUrls.length && !includeUrls.some((part) => url.includes(part))) return false;
  if (excludeUrls.some((part) => url.includes(part))) return false;
  if (!dateMatches(data.blockRuleStart, data.blockRuleEnd)) return false;
  return true;
}

async function evaluateBlock(element) {
  if (!(element instanceof HTMLElement)) return;
  if (window.Shopify?.designMode) {
    element.hidden = false;
    element.classList.remove('commerce-rules-hidden');
    return;
  }
  let visible = blockBaseRulesMatch(element);
  if (visible) {
    const state = element.dataset.blockRuleCartState || 'any';
    const minQuantity = Number(element.dataset.blockRuleCartMinQuantity || 0);
    if (state !== 'any' || minQuantity > 0) {
      try {
        const cart = await getCart();
        if (state === 'empty' && Number(cart.item_count || 0) !== 0) visible = false;
        if (state === 'not_empty' && Number(cart.item_count || 0) === 0) visible = false;
        if (minQuantity > 0 && Number(cart.item_count || 0) < minQuantity) visible = false;
      } catch (error) {
        console.warn('[commerce-section-rules] Block cart condition failed:', error);
        visible = false;
      }
    }
  }
  element.hidden = !visible;
  element.classList.toggle('commerce-rules-hidden', !visible);
  element.dispatchEvent(new CustomEvent('commerce:block-visibility-change', { detail: { visible }, bubbles: true }));
}

function evaluateAllBlocks(root = document) {
  root.querySelectorAll(BLOCK_SELECTOR).forEach((element) => evaluateBlock(element));
}

document.addEventListener('theme:cart-synced', () => evaluateAllBlocks());
document.addEventListener('shopify:section:load', (event) => evaluateAllBlocks(event.target instanceof Element ? event.target : document));
document.addEventListener('DOMContentLoaded', () => evaluateAllBlocks());
if (document.readyState !== 'loading') evaluateAllBlocks();
